/**
 * Utilidades para convertir DataURL a Blob
 * 
 * Convierte un DataURL (p. ej. de canvas) a Blob y obtiene la extensión por MIME.
 * 
 * Uso:
 *   const { blob, ext, isValid } = dataURLtoBlob(croppedImage);
 *   if (isValid && blob.size > 0) {
 *     formData.append('img_url', blob, file?.name || `img_${Date.now()}.${ext}`);
 *   } else if (file) {
 *     formData.append('img_url', file);
 *   }
 * 
 * Si el dataURL es inválido o falla atob(), devuelve blob vacío (size 0) e isValid=false.
 */

const MIME_TO_EXT = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/bmp': 'bmp',
  'image/svg+xml': 'svg',
  'image/tiff': 'tiff',
  'image/x-icon': 'ico',
};

// Tamaño mínimo esperado para una imagen válida (en bytes)
const MIN_VALID_IMAGE_SIZE = 100;

/**
 * Convierte un tipo MIME a su extensión de archivo correspondiente
 * @param {string} mime - Tipo MIME (ej: 'image/jpeg')
 * @returns {string} - Extensión de archivo (ej: 'jpg')
 */
export function mimeToExt(mime) {
  if (!mime || typeof mime !== 'string') return 'jpg';
  const normalized = mime.toLowerCase().trim();
  return MIME_TO_EXT[normalized] || 'jpg';
}

/**
 * Extrae el tipo MIME de un DataURL
 * @param {string} dataURL - DataURL completo
 * @returns {string} - Tipo MIME extraído o 'image/jpeg' por defecto
 */
export function getMimeFromDataURL(dataURL) {
  if (!dataURL || typeof dataURL !== 'string') return 'image/jpeg';
  const match = dataURL.match(/data:([^;]+);/);
  return match ? match[1].trim() : 'image/jpeg';
}

/**
 * Valida si un DataURL tiene el formato correcto
 * @param {string} dataURL - DataURL a validar
 * @returns {boolean} - true si el formato es válido
 */
export function isValidDataURL(dataURL) {
  if (!dataURL || typeof dataURL !== 'string') return false;
  if (!dataURL.startsWith('data:')) return false;
  
  // Verificar que tiene la estructura correcta: data:[mime];base64,[datos]
  // Simplificado para permitir DataURLs largos sin validar todo el contenido
  const headerRegex = /^data:image\/[a-zA-Z0-9+-]+;base64,/;
  
  if (!headerRegex.test(dataURL)) return false;
  
  // Verificar que hay datos después del header
  const parts = dataURL.split(',');
  if (parts.length !== 2) return false;
  if (!parts[1] || parts[1].length < 50) return false; // Datos muy pequeños
  
  return true;
}

/**
 * Convierte un DataURL a Blob
 * 
 * @param {string} dataURL - DataURL a convertir (ej: 'data:image/jpeg;base64,...')
 * @returns {Object} - { blob: Blob, ext: string, isValid: boolean, error?: string }
 */
export function dataURLtoBlob(dataURL) {
  // Resultado por defecto para errores
  const errorResult = (error) => ({
    blob: new Blob(),
    ext: 'jpg',
    isValid: false,
    error
  });

  // Validaciones iniciales
  if (!dataURL) {
    return errorResult('DataURL está vacío');
  }

  if (typeof dataURL !== 'string') {
    return errorResult('DataURL no es una cadena de texto');
  }

  if (!dataURL.startsWith('data:')) {
    return errorResult('DataURL no tiene el prefijo correcto');
  }

  // Verificar formato básico
  if (!dataURL.includes(';base64,')) {
    return errorResult('DataURL no contiene datos base64');
  }

  try {
    // Separar header y datos
    const commaIndex = dataURL.indexOf(',');
    if (commaIndex === -1) {
      return errorResult('No se encontró el separador de datos');
    }

    const header = dataURL.substring(0, commaIndex);
    const base64Data = dataURL.substring(commaIndex + 1);

    // Verificar que hay datos
    if (!base64Data || base64Data.length === 0) {
      return errorResult('No hay datos base64');
    }

    // Extraer tipo MIME
    const mimeMatch = header.match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1].trim() : 'image/jpeg';

    // Decodificar base64
    let binaryString;
    try {
      binaryString = atob(base64Data);
    } catch (e) {
      return errorResult('Error al decodificar base64: ' + e.message);
    }

    // Verificar tamaño mínimo
    if (binaryString.length < MIN_VALID_IMAGE_SIZE) {
      return errorResult(`Datos demasiado pequeños: ${binaryString.length} bytes`);
    }

    // Convertir a array de bytes
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Crear blob
    const blob = new Blob([bytes], { type: mime });

    // Verificar que el blob tiene tamaño válido
    if (blob.size < MIN_VALID_IMAGE_SIZE) {
      return errorResult(`Blob demasiado pequeño: ${blob.size} bytes`);
    }

    return {
      blob,
      ext: mimeToExt(mime),
      isValid: true,
      mime
    };

  } catch (error) {
    console.error('Error en dataURLtoBlob:', error);
    return errorResult('Error inesperado: ' + (error.message || 'desconocido'));
  }
}

/**
 * Convierte un Blob a DataURL
 * 
 * @param {Blob} blob - Blob a convertir
 * @returns {Promise<string>} - Promise que resuelve con el DataURL
 */
export function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    if (!blob || !(blob instanceof Blob)) {
      reject(new Error('Entrada no es un Blob válido'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Error al leer el Blob'));
    reader.readAsDataURL(blob);
  });
}
